import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, FileText, Copy, Trash2, Calendar, Eye, Download, CheckCircle2, ChevronDown, Sparkles, Loader2, Share2, MoreHorizontal } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { API } from '../App';

const ContentLibrary = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [blogToSchedule, setBlogToSchedule] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [socialPosts, setSocialPosts] = useState([]);
  const [generatingSocial, setGeneratingSocial] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(null);
  const [imagePrompt, setImagePrompt] = useState('');
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [uploadingImage, setUploadingImage] = useState(null);
  const [publishDropdownOpen, setPublishDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchLibrary();
  }, [user, navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (publishDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setPublishDropdownOpen(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [publishDropdownOpen]);

  const fetchLibrary = async () => {
    try {
      const response = await axios.get(`${API}/library/${user.id}`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching library:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBlog = (blog) => {
    setSelectedBlog(blog);
    setShowModal(true);
  };

  const handleCopyBlog = (blog) => {
    const fullContent = `${blog.title}\n\n${blog.content}\n\n${blog.cta_section}`;
    
    // Use textarea fallback method (more reliable)
    const textarea = document.createElement('textarea');
    textarea.value = fullContent;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      alert('Blog copied to clipboard!');
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Could not copy. Please select and copy manually.');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!confirm('Are you sure you want to delete this blog?')) return;
    
    try {
      await axios.delete(`${API}/library/${blogId}`);
      await fetchLibrary();
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Error deleting blog');
    }
  };

  const handleExport = async (blogId, format) => {
    try {
      const blog = blogs.find(b => b.id === blogId);
      const extension = format === 'markdown' ? 'md' : format;
      const filename = `${blog?.slug || 'blog'}.${extension}`;
      
      // Fetch the file
      const downloadUrl = `${API}/library/blog/${blogId}/export/${format}`;
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error exporting blog:', error);
      alert(`Export failed: ${error.message}`);
    }
  };

  const handleExportAll = async (format) => {
    if (!confirm(`Export all ${blogs.length} blogs as ${format.toUpperCase()}? This may take a moment.`)) return;
    
    setExportingAll(true);
    try {
      const downloadUrl = `${API}/library/export-all/${user.id}?format=${format}`;
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all-blogs.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error exporting all blogs:', error);
      alert(`Export failed: ${error.message}`);
    } finally {
      setExportingAll(false);
    }
  };

  const handlePublish = async (blogId) => {
    try {
      // Update blog status to published
      await axios.put(`${API}/library/blog/${blogId}`, {
        status: 'published'
      });
      alert('✅ Blog marked as published!');
      await fetchLibrary();
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Error marking blog as published');
    }
  };

  const [publishingToDuda, setPublishingToDuda] = useState(null);
  const [showDudaPublishModal, setShowDudaPublishModal] = useState(false);
  const [dudaPublishBlog, setDudaPublishBlog] = useState(null);
  const [dudaScheduleDate, setDudaScheduleDate] = useState('');
  const [selectedCta, setSelectedCta] = useState('');
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  // Fetch user profile for CTAs and Links
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API}/profile/${user.id}`);
          setUserProfile(response.data);
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
    };
    fetchProfile();
  }, [user]);

  const handlePublishToDuda = async (blogId) => {
    // Find the blog and open the publish modal
    const blog = blogs.find(b => b.id === blogId);
    setDudaPublishBlog(blog);
    setSelectedCta('');
    setSelectedLinks([]);
    setDudaScheduleDate('');
    setShowDudaPublishModal(true);
  };

  const handleDudaPublishConfirm = async () => {
    if (!dudaPublishBlog) return;
    
    setPublishingToDuda(dudaPublishBlog.id);
    try {
      const response = await axios.post(`${API}/duda/publish/${dudaPublishBlog.id}`, {
        publish_date: dudaScheduleDate || null,
        cta_id: selectedCta || null,
        link_ids: selectedLinks.length > 0 ? selectedLinks : null
      });
      
      if (response.data.scheduled) {
        alert(
          '📅 Successfully Scheduled on Duda!\n\n' +
          `📝 Title: ${response.data.title}\n` +
          `📆 Publish Date: ${response.data.publish_date}\n\n` +
          'Post will auto-publish at the scheduled time.'
        );
      } else {
        alert(
          '✅ Successfully Published to Duda!\n\n' +
          `📝 Title: ${response.data.title}\n\n` +
          '⚠️ Note: Post is saved as DRAFT.\n' +
          'Go to Duda to review and publish it live.'
        );
      }
      setShowDudaPublishModal(false);
      setDudaPublishBlog(null);
    } catch (error) {
      console.error('Error publishing to Duda:', error);
      alert(`❌ Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setPublishingToDuda(null);
    }
  };

  const toggleLinkSelection = (linkId) => {
    setSelectedLinks(prev => 
      prev.includes(linkId) 
        ? prev.filter(id => id !== linkId)
        : [...prev, linkId]
    );
  };

  const handleScheduleClick = (blog) => {
    setBlogToSchedule(blog);
    setScheduledDate('');
    setShowScheduleModal(true);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduledDate || !blogToSchedule) {
      alert('Please select a date');
      return;
    }

    try {
      await axios.post(`${API}/calendar/schedule?user_id=${user.id}`, [{
        question: blogToSchedule.question,
        scheduled_date: scheduledDate,
        blog_id: blogToSchedule.id
      }]);
      
      alert(`✅ Blog scheduled for ${scheduledDate}`);
      setShowScheduleModal(false);
      setBlogToSchedule(null);
    } catch (error) {
      console.error('Error scheduling:', error);
      alert('Error scheduling blog');
    }
  };

  const handleGenerateSocial = async (blog) => {
    setSelectedBlog(blog);
    setGeneratingSocial(true);
    setShowSocialModal(true);
    
    try {
      // Check if posts already exist
      const existingResponse = await axios.get(`${API}/social/${blog.id}?user_id=${user.id}`);
      
      if (existingResponse.data.count > 0) {
        // Posts already exist, just show them
        setSocialPosts(existingResponse.data.posts);
      } else {
        // Generate new posts
        const response = await axios.post(`${API}/social/generate/${blog.id}?user_id=${user.id}`);
        setSocialPosts(response.data.posts);
      }
    } catch (error) {
      console.error('Error generating social posts:', error);
      alert('Error generating social posts. Please try again.');
      setShowSocialModal(false);
    } finally {
      setGeneratingSocial(false);
    }
  };

  const handleUpdateSocialPost = async (postId, updates) => {
    try {
      await axios.put(`${API}/social/${postId}?user_id=${user.id}`, updates);
      
      // Update local state
      setSocialPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, ...updates } : post
      ));
      
      setEditingPostId(null);
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Error updating post');
    }
  };

  const handleGenerateImage = async (postId, prompt = null) => {
    setGeneratingImage(postId);
    try {
      const url = prompt 
        ? `${API}/social/generate-image/${postId}?custom_prompt=${encodeURIComponent(prompt)}`
        : `${API}/social/generate-image/${postId}`;
      
      const response = await axios.post(url);
      
      // Refresh the social posts to show the new image
      if (showSocialModal && socialPosts.length > 0) {
        const blogId = socialPosts[0].blog_id;
        const postsResponse = await axios.get(`${API}/social/${blogId}`);
        setSocialPosts(postsResponse.data);
      }
      
      setEditingPromptId(null);
      setCustomPrompt('');
      alert('✅ Image generated successfully!');
    } catch (error) {
      console.error('Error generating image:', error);
      alert(`Error generating image: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGeneratingImage(null);
    }
  };

  const handleUploadImage = (postId, file) => {
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image file size must be less than 5MB');
      return;
    }
    
    setUploadingImage(postId);
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64String = reader.result;
        
        await axios.post(`${API}/social/upload-image/${postId}?image_data=${encodeURIComponent(base64String)}`);
        
        // Refresh the social posts to show the new image
        if (showSocialModal && socialPosts.length > 0) {
          const blogId = socialPosts[0].blog_id;
          const postsResponse = await axios.get(`${API}/social/${blogId}`);
          setSocialPosts(postsResponse.data);
        }
        
        alert('✅ Image uploaded successfully!');
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(`Error uploading image: ${error.response?.data?.detail || error.message}`);
      } finally {
        setUploadingImage(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteSocialPost = async (postId) => {
    if (!confirm('Delete this social post?')) return;
    
    try {
      await axios.delete(`${API}/social/${postId}?user_id=${user.id}`);
      setSocialPosts(prev => prev.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Error deleting post');
    }
  };

  const handleCopySocialPost = (post) => {
    const text = `${post.post_text}\n\n${post.hashtags}`;
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      alert('Post copied to clipboard!');
    } catch (err) {
      alert('Could not copy. Please select and copy manually.');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleCopyAllSocialPosts = () => {
    const allText = socialPosts.map(post => 
      `${post.post_text}\n${post.hashtags}\n\n---\n`
    ).join('\n');
    
    const textarea = document.createElement('textarea');
    textarea.value = allText;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      alert(`All ${socialPosts.length} posts copied!`);
    } catch (err) {
      alert('Could not copy. Please select and copy manually.');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-4 min-w-0">
              <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="flex-shrink-0">
                <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <div className="flex items-center space-x-2 min-w-0">
                <FileText className="w-5 h-5 md:w-6 md:h-6 text-blue-600 flex-shrink-0" />
                <span className="text-base md:text-xl font-bold text-gray-900 truncate">Content Library</span>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
              {blogs.length > 0 && (
                <Button 
                  onClick={() => handleExportAll('docx')}
                  disabled={exportingAll}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs md:text-sm"
                  size="sm"
                >
                  <Download className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Export All (Word)</span>
                  <span className="sm:hidden">Export</span>
                </Button>
              )}
              <Button 
                onClick={() => navigate('/content-calendar')}
                variant="outline"
                size="sm"
                className="hidden sm:flex"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Calendar
              </Button>
              <Button 
                onClick={() => navigate('/integrations')}
                variant="outline"
                size="sm"
                className="text-xs md:text-sm"
              >
                <span className="hidden sm:inline">Integrations</span>
                <span className="sm:hidden">Connect</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm text-gray-600">Total Blogs</span>
              <FileText className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">{blogs.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm text-gray-600">Total Words</span>
              <FileText className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              {blogs.reduce((sum, b) => sum + b.word_count, 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm text-gray-600">Published</span>
              <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              {blogs.filter(b => b.status === 'published').length}
            </p>
          </div>
        </div>

        {/* Blog List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading your content...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Content Yet</h3>
            <p className="text-gray-600 mb-6">
              Generate your first blog from a keyword research question!
            </p>
            <Button 
              onClick={() => navigate('/keyword-research')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Start Keyword Research →
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {blogs.map((blog) => (
              <div key={blog.id} className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 break-words">{blog.title}</h3>
                      {blog.status === 'published' && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0">
                          Published
                        </span>
                      )}
                    </div>
                    <p className="text-xs md:text-sm text-gray-600 mb-3 line-clamp-2">
                      <strong>Question:</strong> {blog.question}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3 md:w-4 md:h-4" />
                        {blog.word_count.toLocaleString()} words
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                        {formatDate(blog.generated_at)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 w-full md:w-auto">
                    {/* Icon Row - Quick Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewBlog(blog)}
                        className="p-2"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopyBlog(blog)}
                        className="p-2"
                        title="Copy"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGenerateSocial(blog)}
                        className="p-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                        title="Social Media"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleScheduleClick(blog)}
                        className="p-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                        title="Schedule"
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteBlog(blog.id)}
                        className="p-2 border-red-300 text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Publish / Export Dropdown */}
                    <div className="relative" ref={publishDropdownOpen === blog.id ? dropdownRef : null}>
                      <Button
                        size="sm"
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white text-xs justify-between"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPublishDropdownOpen(publishDropdownOpen === blog.id ? null : blog.id);
                        }}
                      >
                        <span className="flex items-center">
                          <Share2 className="w-3 h-3 mr-1" />
                          Publish / Export
                        </span>
                        <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${publishDropdownOpen === blog.id ? 'rotate-180' : ''}`} />
                      </Button>
                      
                      {publishDropdownOpen === blog.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                          {/* Publish Section */}
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Publish To
                          </div>
                          <button
                            onClick={() => {
                              handlePublishToDuda(blog.id);
                              setPublishDropdownOpen(null);
                            }}
                            disabled={publishingToDuda === blog.id}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            🔥 Studio Sites
                          </button>
                          <button
                            onClick={() => {
                              handlePublishToDuda(blog.id);
                              setPublishDropdownOpen(null);
                            }}
                            disabled={publishingToDuda === blog.id}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            🌐 Duda
                          </button>
                          <button
                            onClick={() => {
                              alert('WordPress integration coming soon! Go to Integrations to connect your WordPress site.');
                              setPublishDropdownOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-400"
                          >
                            📝 WordPress
                          </button>
                          <button
                            onClick={() => {
                              alert('Wix integration coming soon!');
                              setPublishDropdownOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-400"
                          >
                            🔷 Wix
                          </button>
                          {blog.status !== 'published' && (
                            <button
                              onClick={() => {
                                handlePublish(blog.id);
                                setPublishDropdownOpen(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-purple-600"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              Mark as Published
                            </button>
                          )}
                          
                          {/* Divider */}
                          <div className="border-t border-gray-200 my-1"></div>
                          
                          {/* Export Section */}
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Export As
                          </div>
                          <button
                            onClick={() => {
                              handleExport(blog.id, 'docx');
                              setPublishDropdownOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                            Word Document
                          </button>
                          <button
                            onClick={() => {
                              handleExport(blog.id, 'html');
                              setPublishDropdownOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Download className="w-4 h-4 text-green-600" />
                            HTML
                          </button>
                          <button
                            onClick={() => {
                              handleExport(blog.id, 'pdf');
                              setPublishDropdownOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Download className="w-4 h-4 text-red-600" />
                            PDF
                          </button>
                          
                          {/* Divider */}
                          <div className="border-t border-gray-200 my-1"></div>
                          
                          {/* Delete */}
                          <button
                            onClick={() => {
                              handleDeleteBlog(blog.id);
                              setPublishDropdownOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Blog
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Blog Modal */}
      {showModal && selectedBlog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedBlog.title}</h2>
                <p className="text-gray-600">
                  <strong>Question:</strong> {selectedBlog.question}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                  <span>{selectedBlog.word_count.toLocaleString()} words</span>
                  <span>{formatDate(selectedBlog.generated_at)}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowModal(false)}
              >
                ✕
              </Button>
            </div>

            <div className="space-y-6">
              {/* Featured Image */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Featured Image</h3>
                
                {/* Image Prompt Editor */}
                <div className="mb-4">
                  <label className="text-sm text-gray-600 mb-1 block">Image Prompt (edit to customize):</label>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    rows={4}
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder={`Vibrant, ultra-realistic, emotionally engaging photo, 4K UHD. A warm, cinematic scene in a modern dental office. A friendly dentist in professional attire explains a procedure to a relaxed patient in a comfortable chair. Warm natural light streaming through large windows, shallow depth of field. Mood: trust, comfort, professionalism, care. No text or logos.`}
                  />
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        setGeneratingImage(selectedBlog.id);
                        const prompt = imagePrompt || '';
                        const response = await fetch(`${API}/generate/blog-image`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            blog_id: selectedBlog.id,
                            title: selectedBlog.title,
                            industry: user?.industry || 'business',
                            custom_prompt: prompt
                          })
                        });
                        const data = await response.json();
                        if (data.success) {
                          setSelectedBlog({ ...selectedBlog, featured_image_url: data.image_url });
                          fetchLibrary();
                        } else {
                          alert('Image generation failed. Please try again.');
                        }
                      } catch (error) {
                        console.error('Error generating image:', error);
                        alert('Image generation failed. Please try again.');
                      } finally {
                        setGeneratingImage(null);
                      }
                    }}
                    disabled={generatingImage === selectedBlog.id || uploadingImage === selectedBlog.id}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {generatingImage === selectedBlog.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Generating (up to 60s)...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-1" />
                        {selectedBlog.featured_image_url ? 'Regenerate' : 'Generate'} with AI
                      </>
                    )}
                  </Button>
                  
                  <span className="text-gray-400">or</span>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        try {
                          setUploadingImage(selectedBlog.id);
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const response = await fetch(`${API}/blog/${selectedBlog.id}/upload-image`, {
                            method: 'POST',
                            body: formData
                          });
                          const data = await response.json();
                          if (data.success) {
                            setSelectedBlog({ ...selectedBlog, featured_image_url: data.image_url });
                            fetchLibrary();
                          } else {
                            alert('Upload failed. Please try again.');
                          }
                        } catch (error) {
                          console.error('Error uploading image:', error);
                          alert('Upload failed. Please try again.');
                        } finally {
                          setUploadingImage(null);
                          e.target.value = '';
                        }
                      }}
                      disabled={uploadingImage === selectedBlog.id || generatingImage === selectedBlog.id}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="pointer-events-none"
                      disabled={uploadingImage === selectedBlog.id}
                    >
                      {uploadingImage === selectedBlog.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          📤 Upload Your Own
                        </>
                      )}
                    </Button>
                  </label>
                </div>

                {/* Image Display */}
                {selectedBlog.featured_image_url ? (
                  <img 
                    src={selectedBlog.featured_image_url} 
                    alt={selectedBlog.title}
                    className="w-full h-64 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-full h-48 bg-gray-100 rounded-lg border border-gray-200 flex-col items-center justify-center text-gray-500 ${selectedBlog.featured_image_url ? 'hidden' : 'flex'}`}>
                  <Sparkles className="w-8 h-8 mb-2 text-gray-400" />
                  <span>Edit prompt above, then click "Generate Image"</span>
                </div>
              </div>

              {/* Content */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Content</h3>
                <div className="bg-gray-50 rounded-lg p-6 prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:my-2 prose-strong:text-gray-900 [&>p]:max-w-prose">
                  <ReactMarkdown>{selectedBlog.content}</ReactMarkdown>
                </div>
              </div>

              {/* CTA */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Call-to-Action</h3>
                <div className="bg-orange-50 rounded-lg p-6 border border-orange-200 prose prose-sm max-w-none">
                  <ReactMarkdown>{selectedBlog.cta_section}</ReactMarkdown>
                </div>
              </div>

              {/* SEO */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">SEO Metadata</h3>
                <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Meta Title</p>
                    <p className="text-sm text-gray-900">{selectedBlog.meta_title}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Meta Description</p>
                    <p className="text-sm text-gray-900">{selectedBlog.meta_description}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Slug</p>
                    <p className="text-sm text-gray-900 font-mono">{selectedBlog.slug}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => handleCopyBlog(selectedBlog)}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Full Blog
                </Button>
                {selectedBlog.status !== 'published' && (
                  <Button
                    onClick={() => handlePublish(selectedBlog.id)}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Publish to Website
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Schedule Blog Post</h3>
            
            {blogToSchedule && (
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">Blog:</p>
                <p className="font-medium text-gray-900 line-clamp-2">{blogToSchedule.title}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Publish Date
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Select when this blog should be published
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowScheduleModal(false);
                  setBlogToSchedule(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleScheduleConfirm}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Duda Publish Modal */}
      {showDudaPublishModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">🌐 Publish to Duda</h3>
            
            {dudaPublishBlog && (
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">Blog:</p>
                <p className="font-medium text-gray-900 line-clamp-2">{dudaPublishBlog.title}</p>
              </div>
            )}

            {/* CTA Selection */}
            {userProfile?.cta_library && userProfile.cta_library.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📢 Select Call-to-Action (Optional)
                </label>
                <select
                  value={selectedCta}
                  onChange={(e) => setSelectedCta(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">No CTA</option>
                  {userProfile.cta_library.map((cta) => (
                    <option key={cta.id} value={cta.id}>
                      {cta.name} ({cta.audience})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Links Selection */}
            {userProfile?.links_library && userProfile.links_library.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔗 Select Links to Include (Optional)
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                  {userProfile.links_library.map((link) => (
                    <label key={link.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedLinks.includes(link.id)}
                        onChange={() => toggleLinkSelection(link.id)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-700 truncate">{link.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Schedule Option */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📅 Schedule (Optional)
              </label>
              <input
                type="datetime-local"
                value={dudaScheduleDate}
                onChange={(e) => setDudaScheduleDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to publish as draft immediately
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowDudaPublishModal(false);
                  setDudaPublishBlog(null);
                  setSelectedCta('');
                  setSelectedLinks([]);
                  setDudaScheduleDate('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDudaPublishConfirm}
                disabled={publishingToDuda}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {publishingToDuda ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    🌐 Publish to Duda
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Posts Modal */}
      {showSocialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Social Media Posts</h2>
                {selectedBlog && (
                  <p className="text-sm text-gray-600 mt-1">For: {selectedBlog.title}</p>
                )}
              </div>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowSocialModal(false);
                  setSocialPosts([]);
                  setSelectedBlog(null);
                }}
              >
                ✕
              </Button>
            </div>

            {generatingSocial ? (
              <div className="text-center py-12">
                <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Generating social media posts...</p>
              </div>
            ) : socialPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No social posts yet</p>
              </div>
            ) : (
              <>
                <div className="mb-6 flex justify-between items-center">
                  <p className="text-sm text-gray-600">{socialPosts.length} posts generated</p>
                  <Button
                    size="sm"
                    onClick={handleCopyAllSocialPosts}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy All Posts
                  </Button>
                </div>

                <div className="space-y-4">
                  {socialPosts.map((post, index) => (
                    <div key={post.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded font-semibold">
                            {post.post_type.toUpperCase()}
                          </span>
                          <div className="flex gap-1 mt-1">
                            {post.platforms?.map(platform => (
                              <span key={platform} className="text-xs text-gray-500">
                                {platform === 'facebook' && '📘'}
                                {platform === 'instagram' && '📷'}
                                {platform === 'twitter' && '🐦'}
                                {platform === 'linkedin' && '💼'}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCopySocialPost(post)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSocialPost(post.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Editable Post Text */}
                      {editingPostId === post.id ? (
                        <textarea
                          className="w-full p-3 border border-blue-300 rounded-lg mb-2 focus:ring-2 focus:ring-blue-500"
                          rows={4}
                          defaultValue={post.post_text}
                          onBlur={(e) => handleUpdateSocialPost(post.id, { post_text: e.target.value })}
                          autoFocus
                        />
                      ) : (
                        <p
                          className="text-gray-800 mb-2 whitespace-pre-wrap cursor-pointer hover:bg-gray-50 p-2 rounded"
                          onClick={() => setEditingPostId(post.id)}
                        >
                          {post.post_text}
                        </p>
                      )}

                      {/* Editable Hashtags */}
                      {editingPostId === post.id ? (
                        <input
                          type="text"
                          className="w-full p-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          defaultValue={post.hashtags}
                          onBlur={(e) => handleUpdateSocialPost(post.id, { hashtags: e.target.value })}
                        />
                      ) : (
                        <p
                          className="text-blue-600 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded"
                          onClick={() => setEditingPostId(post.id)}
                        >
                          {post.hashtags}
                        </p>
                      )}

                      {/* Generated Image */}
                      {post.image_url && (
                        <div className="mt-3">
                          <img 
                            src={post.image_url} 
                            alt="Generated social media image" 
                            className="max-w-xs rounded-lg border border-gray-200"
                          />
                        </div>
                      )}

                      {/* Image Prompt & Generation */}
                      {post.image_prompt && (
                        <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className="text-xs font-semibold text-purple-900">📷 Image Prompt</span>
                            <div className="flex gap-2">
                              {editingPromptId === post.id ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleGenerateImage(post.id, customPrompt || post.image_prompt)}
                                    disabled={generatingImage === post.id}
                                    className="text-xs bg-purple-600 hover:bg-purple-700 text-white"
                                  >
                                    {generatingImage === post.id ? (
                                      <>
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        Generating...
                                      </>
                                    ) : (
                                      <>
                                        <Sparkles className="w-3 h-3 mr-1" />
                                        Generate
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingPromptId(null);
                                      setCustomPrompt('');
                                    }}
                                    className="text-xs"
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setEditingPromptId(post.id);
                                      setCustomPrompt(post.image_prompt);
                                    }}
                                    className="text-xs bg-purple-600 hover:bg-purple-700 text-white"
                                  >
                                    <Sparkles className="w-3 h-3 mr-1" />
                                    {post.image_url ? 'Regenerate' : 'Generate Image'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const textarea = document.createElement('textarea');
                                      textarea.value = post.image_prompt;
                                      textarea.style.position = 'fixed';
                                      textarea.style.opacity = '0';
                                      document.body.appendChild(textarea);
                                      textarea.select();
                                      try {
                                        document.execCommand('copy');
                                        alert('Image prompt copied!');
                                      } catch (err) {
                                        alert('Could not copy');
                                      } finally {
                                        document.body.removeChild(textarea);
                                      }
                                    }}
                                    className="text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    Copy
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {editingPromptId === post.id ? (
                            <div className="mt-2">
                              <textarea
                                value={customPrompt}
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                className="w-full text-xs p-2 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                rows={4}
                                placeholder="Edit the image prompt (add uniform details, colors, style, etc.)"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                💡 Add details like: "modern office setting", "warm lighting", "professional photo quality"
                              </p>
                            </div>
                          ) : (
                            <>
                              <p className="text-xs text-gray-700 italic">{post.image_prompt}</p>
                              
                              {/* Upload Custom Image Option */}
                              <div className="mt-2 pt-2 border-t border-purple-200">
                                <label className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-purple-300 rounded cursor-pointer hover:bg-purple-50 transition-colors">
                                  <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleUploadImage(post.id, e.target.files[0]);
                                        e.target.value = '';
                                      }
                                    }}
                                    className="hidden"
                                    disabled={uploadingImage === post.id}
                                  />
                                  {uploadingImage === post.id ? (
                                    <>
                                      <Loader2 className="w-3 h-3 animate-spin text-purple-600" />
                                      <span className="text-xs text-purple-600">Uploading...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Download className="w-3 h-3 text-purple-600" />
                                      <span className="text-xs text-purple-700 font-medium">
                                        {post.image_url ? 'Replace with Custom Image' : 'Upload Custom Image'}
                                      </span>
                                    </>
                                  )}
                                </label>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Schedule Date */}
                      <div className="mt-3 flex items-center gap-2">
                        {post.scheduled_date ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              📅 Scheduled: {post.scheduled_date}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUpdateSocialPost(post.id, { scheduled_date: null, status: 'draft' })}
                              className="text-xs"
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              min={new Date().toISOString().split('T')[0]}
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleUpdateSocialPost(post.id, { 
                                    scheduled_date: e.target.value,
                                    status: 'scheduled'
                                  });
                                }
                              }}
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            />
                            <span className="text-xs text-gray-500">Schedule this post</span>
                          </div>
                        )}
                      </div>

                      {/* Character Count */}
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span>Characters: {post.character_count}</span>
                        {post.character_count > 280 && <span className="text-red-600">⚠️ Too long for Twitter</span>}
                        {post.character_count > 125 && post.platforms?.includes('instagram') && (
                          <span className="text-yellow-600">⚠️ Long for Instagram preview</span>
                        )}
                      </div>

                      {editingPostId === post.id && (
                        <p className="text-xs text-gray-500 mt-2">Click outside to save changes</p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSocialModal(false);
                  setSocialPosts([]);
                  setSelectedBlog(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentLibrary;
